import React, { useCallback, useEffect, useState } from 'react';
import Collapse from '../../atoms/Collapse/Collapse';
import Button from '../../atoms/Button';
import { css } from '@emotion/react';
import { generateUniqueId } from '../../../utils/utility';
import { useSortFilterContext } from './StateContext';
import { Popover, Radio, RadioChangeEvent } from 'antd';
import Chevron from '../../atoms/Icon/Chevron';
import Cascader from '../../atoms/Cascader';
import { getY1Options } from '../../../utils/KPICascader';
import { RightOutlined } from '@ant-design/icons';
import Tooltip from '../../atoms/Tooltip/Tooltip';

type SortKey = {
    key: 'ascending' | 'descending';
    metric: string;
    dataType: string;
    lag: number;
    name: string;
};

type Props = {
    defaultOption: SortKey;
    sortingOptions: string[];
    metricOptions: any[];
};

const SortByDropDown: React.FC<Props> = (props) => {
    const [showSelected, setShowSelected] = useState(true);

    const [selectedOption, setSelectedOption] = useState(props.defaultOption);

    const [activeKey, setActiveKey] = useState<string | undefined>();
    const { sortFilterDispatch, sortFilterState } = useSortFilterContext();
    const sortingStates = [
        { key: 'ascending', label: 'Lowest to Highest' },
        { key: 'descending', label: 'Highest to Lowest' },
    ];

    useEffect(() => {
        setSelectedOption(sortFilterState.sortKey);
    }, [sortFilterState.sortKey]);

    const getSortingLabel = (key: string) => {
        let sortItem = sortingStates.find((item) => item.key == key);
        return sortItem?.label;
    };

    const handleStateChange = (key: string | string[]) => {
        if (key.length && showSelected) {
            setShowSelected(false);
        } else {
            setShowSelected(true);
        }
        setActiveKey(key[0]);
    };

    const handleRadioChange = (e: RadioChangeEvent) => {
        setSelectedOption((prev) => {
            let newSelectedOption = {
                ...prev,
                key: e.target.value,
            };
            return newSelectedOption;
        });

        const payload = {
            key: e.target.value,
            metric: sortFilterState.sortKey.metric,
            dataType: sortFilterState.sortKey.dataType,
            lag: sortFilterState.sortKey.lag,
            name: sortFilterState.sortKey.name,
        };

        sortFilterDispatch({ type: 'UPDATE_SORT_BY', payload: payload });
    };

    const getChildren = () => {
        return (
            <Radio.Group
                onChange={handleRadioChange}
                value={sortFilterState?.sortKey?.key}
                data-test-id="sort-radio-group"
            >
                {props.sortingOptions.map((option, index) => (
                    <Radio
                        className="py-[0.3125rem] cursor-pointer font-sans font-normal text-sm mt-2"
                        onClick={() => {
                            // handleStateChange([]);
                            // setSelectedOption(option);
                            // setShowApply(true);
                        }}
                        key={generateUniqueId()}
                        value={sortingStates[index].key}
                        data-test-id={`sort-radio-${sortingStates[index].key}`}
                    >
                        {option}
                    </Radio>
                ))}
            </Radio.Group>
        );
    };

    const onCascaderChange = (value: any[], selectedOptions: any[]) => {
        const option = getY1Options(value, selectedOptions);

        const payload = {
            key: selectedOption.key,
            ...option,
        };
        setSelectedOption((prev) => {
            let newSelectedOption = {
                ...prev,
                ...option,
            };
            return newSelectedOption;
        });

        sortFilterDispatch({ type: 'UPDATE_SORT_BY', payload: payload });
        setShowSelected(true);
        setActiveKey(undefined);
    };

    const getChildrenCallback = useCallback(getChildren, [sortFilterState.sortKey]);

    return (
        <div className="p-4 flex flex-col w-full" data-test-id="sort-by-dropdown">
            <Collapse
                ghost
                expandIconPosition="end"
                items={[{ key: 'sort', label: 'Sort By ', children: getChildrenCallback() }]}
                className="w-full"
                onChange={handleStateChange}
                activeKey={activeKey}
                data-test-id="sort-collapse"
                expandIcon={({ isActive }) => (
                    <RightOutlined data-test-id="sort-by-collapse-arrow" rotate={isActive ? 90 : 0} />
                )}
            />
            {showSelected ? (
                <p className="text-sm leading-[1.375rem] text-black/[0.45]" data-test-id="selected-sort-label">
                    {getSortingLabel(selectedOption?.key)}
                </p>
            ) : null}
            {showSelected ? (
                <p className="text-sm leading-[1.375rem] text-black/[0.45]" data-test-id="selected-metric-name">
                    {selectedOption?.name}
                </p>
            ) : null}

            {!showSelected ? (
                <div className="pt-[1.19rem] w-36 ml-2" data-test-id="metric-selector-container">
                    <Popover
                        align={{ offset: [30, 0] }}
                        trigger={'click'}
                        placement="rightTop"
                        content={
                            <Cascader.Panel
                                style={{ border: '0px' }}
                                options={props.metricOptions}
                                onChange={onCascaderChange}
                                data-test-id="metric-cascader"
                            />
                        }
                    >
                        <Button
                            className=" border-[#D9D9D9] text-base leading-6 py-1 font-semibold font-sans rounded-lg shadow-[0px_2px_0px_0px_#00000005] text-opacity-85 w-60"
                            css={css`
                                &:hover {
                                    border-color: #d9d9d9 !important;
                                    color: rgba(0, 0, 0, 0.88) !important;
                                }
                            `}
                            icon={<Chevron rotate={90} />}
                            iconPosition="end"
                            disabled={!selectedOption.key}
                            data-test-id="metric-select-button"
                        >
                            <Tooltip title={selectedOption?.name || `Select Metric`} trigger={'hover'}>
                                <p className=" w-full truncate">{selectedOption?.name || `Select Metric`}</p>
                            </Tooltip>
                        </Button>
                    </Popover>
                </div>
            ) : null}
        </div>
    );
};

export default SortByDropDown;
